import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import { ApiResListsType } from 'global-services/api/OhsApiModels';
import OhsReviewPlanRecord, {
  OhsReviewPlansFilterPayload,
} from 'review-plan/models/OhsReviewPlanRecord';
import { OhsGlobalSearchFilter, OhsGlobalSearchPayload } from 'search/OhsSearchModels';
import { initialGlobalSearchPayload } from 'search/store/OhsSearchSlice';
import { ModuleType, TierType } from 'global-services/constants/OhsObject';
import { OhsTabKeyTypes } from 'global-components/tab/OhsTabModels';
import { OhsUser } from 'user/OhsUser';
import OhsUserLocalServices from 'user/OhsUserLocalServices';

import getReviewPlansList, {
  getReviewPlansAllocatedList,
  getReviewPlanSearch,
} from './OhsReviewPlanServices';
import parseListAllocations from './utils/parseListAllocations';
import {
  OhsReviewPlanSearchMain,
  ReviewPlanTabKeyTypes,
} from './register/search/OhsReviewPlanFilterModels';

export interface ReviewPlanListsRecordState {
  isLoading: boolean;
  reviewPlanList: ApiResListsType<any[]> | null;
  currentPage: number;
  currentTab: OhsTabKeyTypes;
}

export const defaultTaskSearchFilter: OhsGlobalSearchFilter = {
  ...initialGlobalSearchPayload.filter,
  modules: [ModuleType.ReviewPlan],
};
export const initialReviewPlanSearch: OhsGlobalSearchPayload = {
  searchKey: '',
  substringSearch: true,
  highlight: false,
  filter: defaultTaskSearchFilter,
};

const initialState: ReviewPlanListsRecordState = {
  isLoading: false,
  reviewPlanList: null,
  currentPage: 1,
  currentTab: OhsTabKeyTypes.List,
};

export const getViewPreset = (tierNum: number) => {
  switch (tierNum) {
    case 1:
      return 'view_1';
    case 2:
      return 'view_2';
    case 3:
      return 'view_3';
    case 4:
      return 'view_4';
    default:
      return undefined;
  }
};

const getReviewPlansWithAllocations = (
  filters: OhsReviewPlansFilterPayload | OhsReviewPlanSearchMain,
  hasSearch: boolean
): Promise<ApiResListsType<any[]> | null> => {
  const getReviewPlansListReq = async (): Promise<ApiResListsType<any[]> | null> => {
    const reviewPlansList = hasSearch
      ? await getReviewPlanSearch(filters as OhsReviewPlanSearchMain)
      : await getReviewPlansList(filters as OhsReviewPlansFilterPayload);

    if (reviewPlansList && reviewPlansList?.items.length !== 0) {
      const getIDs: string[] = [];
      reviewPlansList?.items.forEach((item: OhsReviewPlanRecord) => {
        if (item._id && item.tier?.type === TierType.T3) {
          getIDs.push(item._id);
        }
      });
      if (getIDs && getIDs.length === 0) {
        return new Promise((resolve) => {
          resolve(reviewPlansList);
        });
      }
      const allocatedList: any = await getReviewPlansAllocatedList(getIDs);

      const reviewPlansListWithAllocations = parseListAllocations(
        reviewPlansList,
        allocatedList
      ) as ApiResListsType<OhsReviewPlanRecord[]> | null;

      return new Promise((resolve) => {
        resolve(reviewPlansListWithAllocations);
      });
    }
    return new Promise((resolve) => {
      resolve(reviewPlansList);
    });
  };

  return getReviewPlansListReq();
};

const reviewPlanListRPC = async (
  filters: OhsReviewPlansFilterPayload | OhsReviewPlanSearchMain,
  hasSearch: boolean
): Promise<any> => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;

  if (userTier && userTier === 2 && user?.configs.reviewplan?.PERMISSIONS.view) {
    const response = hasSearch
      ? await getReviewPlanSearch(filters as OhsReviewPlanSearchMain)
      : await getReviewPlansList(filters as OhsReviewPlansFilterPayload);
    return response;
  }
  if (userTier && userTier === 3 && user?.configs.reviewplan?.PERMISSIONS.view) {
    const response = await getReviewPlansWithAllocations(filters, hasSearch);
    return response;
  }
  if (userTier && userTier === 4 && user?.configs.reviewplan?.PERMISSIONS.view) {
    const response = hasSearch
      ? await getReviewPlanSearch(filters as OhsReviewPlanSearchMain)
      : await getReviewPlansList(filters as OhsReviewPlansFilterPayload);
    return response;
  }

  return null;
};

export const fetchReviewPlanListsAsync = createAsyncThunk<
  {
    list: ApiResListsType<OhsReviewPlanRecord[]> | null;
    count: ApiResListsType<OhsReviewPlanRecord[]> | null;
  },
  {
    list: OhsReviewPlansFilterPayload | OhsReviewPlanSearchMain;
    count: OhsReviewPlansFilterPayload | OhsReviewPlanSearchMain;
    hasSearch: boolean;
    tab: ReviewPlanTabKeyTypes;
  },
  { state: RootState }
>('reviewPlan/fetchReviewPlanList', async (payload, thunkAPI) => {
  try {
    const [list, count] = await Promise.all([
      payload.hasSearch
        ? reviewPlanListRPC(payload.list as OhsReviewPlanSearchMain, true)
        : reviewPlanListRPC(payload.list as OhsReviewPlansFilterPayload, false),
      payload.hasSearch
        ? reviewPlanListRPC(payload.count as OhsReviewPlanSearchMain, true)
        : reviewPlanListRPC(payload.count as OhsReviewPlansFilterPayload, false),
    ]);

    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const reviewPlanSlice = createSlice({
  name: 'reviewPlan',
  initialState,
  reducers: {
    setReviewPlanCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setReviewPlanCurrentTab: (state, action: PayloadAction<OhsTabKeyTypes>) => {
      state.currentTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewPlanListsAsync.pending, (state) => {
        state.isLoading = true;
        state.reviewPlanList = {
          items: [],
          pagination: state.reviewPlanList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchReviewPlanListsAsync.fulfilled, (state, action) => {
        if (action.payload) {
          if (action.payload.list) {
            state.reviewPlanList = action.payload.list;
            let setItemsLastId = '';
            const reviewPlanItems = [...state.reviewPlanList.items];
            if (reviewPlanItems.length > 0) setItemsLastId = reviewPlanItems?.pop()?._id || '';

            state.reviewPlanList.pagination.next = setItemsLastId;
            state.isLoading = false;
          }
          if (action.payload.count && state.reviewPlanList) {
            state.reviewPlanList.pagination.totalPages =
              action.payload.count.pagination.totalPages ?? 0;
          }
        }
      })
      .addCase(fetchReviewPlanListsAsync.rejected, (state) => {
        state.reviewPlanList = null;
      });
  },
});

const reviewPlansState = (state: RootState) => state.reviewplans;
export const getOhsReviewPlansModuleState = createSelector(
  [reviewPlansState],
  (reviewPlans) => reviewPlans
);
export const { setReviewPlanCurrentPage, setReviewPlanCurrentTab } = reviewPlanSlice.actions;
export const reviewPlanModuleReducer = reviewPlanSlice.reducer;
